// Task #6672 — car-detail fetch error classification.
//
// Google was indexing the client-side "Car not found" error render on LIVE
// car pages: when the server prerender transiently fails it (correctly)
// serves the 200 SPA shell, and if Googlebot's client-side car fetch then
// ALSO fails transiently, the page used to render the terminal not-found
// copy with no meta at all — no noindex, no title — so Google indexed the
// error state as the real page ("Bil ikke fundet" titles in SERPs).
//
// This module distinguishes an AUTHORITATIVE 404 from the car API (the
// listing is confirmed gone → terminal "Car not found" copy) from a
// TRANSIENT failure (network error, 5xx, timeout → retry-oriented state
// with a bounded automatic retry).
//
// Task #7587 — only the AUTHORITATIVE 404 render applies `noindex`. The
// transient render leaves the document meta untouched so a rendering-time
// fetch blip (Google WRS frequently drops XHRs) never de-indexes a live
// page. A GSC audit found 20 active listings "Excluded by 'noindex' tag"
// because the old code applied noindex on every error branch; fix is in
// CarDetailPage.tsx useEffect (guard: `if (!isNotFound) return`).
// It is the client-side mirror of `fetchWithTimeoutStatus` in
// scripts/serve-static.mjs (which similarly treats non-404 as transient).

export interface CarDetailFetchError extends Error {
  /** HTTP status attached by api.getCarDetail when the response was received. */
  status?: number;
  /** Slug-redirect sentinel — handled by the page's redirect effect, never an error state. */
  redirect?: string;
}

/**
 * True only when the car API answered with an authoritative HTTP 404 —
 * the listing is confirmed absent/retired. Network failures, timeouts and
 * 5xx responses (no `status`, or a retryable status) are NOT not-found.
 */
export function isAuthoritativeCarNotFound(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  return (error as CarDetailFetchError).status === 404;
}

/**
 * Retry predicate for React Query (`retry` option): bounded automatic
 * retries for transient failures only.
 * - never retry a slug redirect (it navigates, retrying re-throws)
 * - never retry authoritative client errors (404 and other 4xx), EXCEPT
 *   408 (request timeout) and 429 (rate limited), which are transient
 * - retry everything else (network throw = no status, 5xx) up to `maxRetries`
 */
export function shouldRetryCarDetail(failureCount: number, error: unknown, maxRetries = 2): boolean {
  const e = (error && typeof error === "object" ? error : undefined) as CarDetailFetchError | undefined;
  if (e?.redirect) return false;
  const status = e?.status;
  if (status != null && status >= 400 && status < 500 && status !== 408 && status !== 429) return false;
  return failureCount < maxRetries;
}
